/**
 * ShareButton Component
 * WhatsApp-styled share button with loading states and responsive design
 */

import React, { useState, useCallback } from 'react';
import { whatsappService, ShareData, SharingMethod } from '../services/WhatsAppService';
import { notificationService, NotificationType } from '../services/NotificationService';
import './ShareButton.css';

// Extend Window interface for gtag
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}

export interface ShareButtonProps {
  shareType: 'room' | 'result' | 'challenge' | 'invite' | 'achievement';
  data: ShareData;
  position?: 'inline' | 'floating' | 'modal';
  size?: 'small' | 'medium' | 'large';
  customMessage?: string;
  disabled?: boolean;
  onShareStart?: () => void;
  onShareComplete?: (success: boolean) => void;
  onShareError?: (error: Error) => void;
  className?: string;
  style?: React.CSSProperties;
}

export const ShareButton: React.FC<ShareButtonProps> = ({
  shareType,
  data,
  position = 'inline',
  size = 'medium',
  customMessage,
  disabled = false,
  onShareStart,
  onShareComplete,
  onShareError,
  className = '',
  style
}) => {
  const [isSharing, setIsSharing] = useState(false);
  const [shareSuccess, setShareSuccess] = useState<boolean | null>(null);

  const handleShare = useCallback(async () => {
    if (disabled || isSharing) return;

    try {
      setIsSharing(true);
      setShareSuccess(null);
      onShareStart?.();

      // Prepare share data with custom message if provided
      const shareData: ShareData = {
        ...data,
        message: customMessage || data.message
      };

      // Attempt to share via WhatsApp
      const success = await whatsappService.shareToWhatsApp(shareData);

      setShareSuccess(success);
      onShareComplete?.(success);

      if (success) {
        // Show success notification
        notificationService.addNotification({
          type: NotificationType.SUCCESS,
          title: 'Shared Successfully!',
          message: 'Your content has been shared to WhatsApp.',
          duration: 3000
        });

        // Track sharing analytics
        trackShareEvent(shareType, 'whatsapp', success);
      } else {
        // Show fallback notification
        notificationService.addNotification({
          type: NotificationType.INFO,
          title: 'Content Copied',
          message: 'Content copied to clipboard. You can paste it in WhatsApp manually.',
          duration: 4000
        });

        trackShareEvent(shareType, 'clipboard', true);
      }
    } catch (error) {
      console.error('Share failed:', error);
      setShareSuccess(false);
      onShareError?.(error as Error);

      notificationService.addNotification({
        type: NotificationType.ERROR,
        title: 'Share Failed',
        message: 'Unable to share content. Please try again.',
        duration: 4000
      });

      trackShareEvent(shareType, 'error', false);
    } finally {
      setIsSharing(false);
      
      // Reset success state after animation
      setTimeout(() => {
        setShareSuccess(null);
      }, 2000);
    }
  }, [shareType, data, customMessage, disabled, isSharing, onShareStart, onShareComplete, onShareError]);

  const getButtonText = useCallback(() => {
    if (isSharing) return 'Sharing...';
    if (shareSuccess === true) return 'Shared!';
    if (shareSuccess === false) return 'Try Again';
    
    switch (shareType) {
      case 'room':
        return 'Share Room';
      case 'result':
        return 'Share Result';
      case 'challenge':
        return 'Send Challenge';
      case 'invite':
        return 'Invite Friends';
      case 'achievement':
        return 'Share Achievement';
      default:
        return 'Share';
    }
  }, [shareType, isSharing, shareSuccess]);

  const getButtonIcon = useCallback(() => {
    if (isSharing) {
      return (
        <div className="share-button__spinner">
          <div className="spinner"></div>
        </div>
      );
    }
    
    if (shareSuccess === true) {
      return (
        <svg className="share-button__icon share-button__icon--success" viewBox="0 0 24 24" fill="none">
          <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    }
    
    if (shareSuccess === false) {
      return (
        <svg className="share-button__icon share-button__icon--error" viewBox="0 0 24 24" fill="none">
          <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    }

    // WhatsApp icon
    return (
      <svg className="share-button__icon share-button__icon--whatsapp" viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.516"/>
      </svg>
    );
  }, [isSharing, shareSuccess]);

  const buttonClasses = [
    'share-button',
    `share-button--${size}`,
    `share-button--${position}`,
    `share-button--${shareType}`,
    isSharing ? 'share-button--loading' : '',
    shareSuccess === true ? 'share-button--success' : '',
    shareSuccess === false ? 'share-button--error' : '',
    disabled ? 'share-button--disabled' : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <button
      className={buttonClasses}
      onClick={handleShare}
      disabled={disabled || isSharing}
      type="button"
      aria-label={`Share ${shareType} to WhatsApp`}
      title={`Share ${shareType} to WhatsApp`}
      style={style}
    >
      <span className="share-button__content">
        {getButtonIcon()}
        <span className="share-button__text">{getButtonText()}</span>
      </span>
      
      {/* Ripple effect for touch feedback */}
      <span className="share-button__ripple"></span>
    </button>
  );
};

/**
 * Track sharing events for analytics
 */
function trackShareEvent(shareType: string, method: string, success: boolean): void {
  try {
    // Track with Google Analytics if available
    if (window.gtag) {
      window.gtag('event', 'share', {
        method: method,
        content_type: shareType,
        success: success
      });
    }

    // Store local analytics
    const analyticsData = {
      type: 'share',
      shareType,
      method,
      success,
      timestamp: new Date().toISOString()
    };

    const existingAnalytics = JSON.parse(localStorage.getItem('share_analytics') || '[]');
    existingAnalytics.push(analyticsData);

    // Keep only last 100 entries
    if (existingAnalytics.length > 100) {
      existingAnalytics.splice(0, existingAnalytics.length - 100);
    }

    localStorage.setItem('share_analytics', JSON.stringify(existingAnalytics));
  } catch (error) {
    console.error('Failed to track share event:', error);
  }
}

export default ShareButton;